// «Речь» (admin-only, experimental) — transcript produced locally by
// tools/transcribe/transcribe-vod.mjs, served by cloud-run-api GET /transcript.
// Runtime constants live here (not in twitch.model.ts, which stays type-only).
import type { Highlight } from './twitch.model';

/** [start, end, text] or [start, end, text, flags]; seconds are VOD offsets. */
export type TranscriptLine = [number, number, string] | [number, number, string, number];

export const TRANSCRIPT_FLAG_LOOP = 1;
export const TRANSCRIPT_FLAG_LOW_CONF = 2;

export interface TranscriptDoc {
  v: 1;
  vodId: string;
  language: string;
  model: string;
  durationSec: number;
  createdAt: string;
  transcribeSec: number | null;
  stats?: {
    lines: number;
    chars: number;
    words: number;
    collapsedLoops: number;
    droppedHallucinations: number;
    droppedSoft: number;
    lowConfidenceLines: number;
  };
  lines: TranscriptLine[];
}

// Fixed taxonomy keeps lane colors / filter chips stable across independently analyzed chunks;
// the open-endedness lives in the per-segment free-form `transcriptTag`. Mirror of CATEGORIES
// in cloud-run-api/lib/transcript.js.
export type TranscriptCategory = 'funny' | 'story' | 'emotion' | 'opinion' | 'gameplay' | 'other';
export type TranscriptModelKey = 'flash' | 'pro';
export type SpeechMode = 'segments' | 'text' | 'ai-search';

export const TRANSCRIPT_SOURCES = ['transcript-ai', 'transcript-search'] as const;

export function isTranscriptSource(source: Highlight['source'] | undefined): boolean {
  return source === 'transcript-ai' || source === 'transcript-search';
}

// Tailwind class strings MUST be literal full names (JIT scan model — see src/CLAUDE.md).
export interface TranscriptCategoryStyle {
  label: string;
  band: string;        // heatmap lane band + legend dot
  chipOn: string;      // category filter chip, active
  badge: string;       // card badge
  stripe: string;      // card left stripe
}

export const TRANSCRIPT_CATEGORIES: Record<TranscriptCategory, TranscriptCategoryStyle> = {
  funny: {
    label: 'Смешное',
    band: 'bg-amber-400 hover:bg-amber-300',
    chipOn: 'bg-amber-500/20 text-amber-200 border-amber-500/60',
    badge: 'bg-amber-900/50 text-amber-200 border-amber-600/50',
    stripe: 'border-l-amber-400',
  },
  story: {
    label: 'История',
    band: 'bg-sky-400 hover:bg-sky-300',
    chipOn: 'bg-sky-500/20 text-sky-200 border-sky-500/60',
    badge: 'bg-sky-900/50 text-sky-200 border-sky-600/50',
    stripe: 'border-l-sky-400',
  },
  emotion: {
    label: 'Эмоции',
    band: 'bg-rose-500 hover:bg-rose-400',
    chipOn: 'bg-rose-500/20 text-rose-200 border-rose-500/60',
    badge: 'bg-rose-900/50 text-rose-200 border-rose-600/50',
    stripe: 'border-l-rose-500',
  },
  opinion: {
    label: 'Мнение',
    band: 'bg-emerald-400 hover:bg-emerald-300',
    chipOn: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/60',
    badge: 'bg-emerald-900/50 text-emerald-200 border-emerald-600/50',
    stripe: 'border-l-emerald-400',
  },
  gameplay: {
    label: 'Геймплей',
    band: 'bg-violet-400 hover:bg-violet-300',
    chipOn: 'bg-violet-500/20 text-violet-200 border-violet-500/60',
    badge: 'bg-violet-900/50 text-violet-200 border-violet-600/50',
    stripe: 'border-l-violet-400',
  },
  other: {
    label: 'Другое',
    band: 'bg-slate-300 hover:bg-slate-200',
    chipOn: 'bg-slate-500/20 text-slate-200 border-slate-400/60',
    badge: 'bg-slate-800/70 text-slate-200 border-slate-500/50',
    stripe: 'border-l-slate-300',
  },
};

export const TRANSCRIPT_CATEGORY_KEYS: TranscriptCategory[] = ['funny', 'story', 'emotion', 'opinion', 'gameplay', 'other'];

/** AI-search hits over speech (transcript-search) — one style, no category. */
export const TRANSCRIPT_SEARCH_STYLE = {
  band: 'bg-fuchsia-400 hover:bg-fuchsia-300',
  badge: 'bg-fuchsia-900/50 text-fuchsia-200 border-fuchsia-600/50',
  stripe: 'border-l-fuchsia-400',
};
